<template>
  <b-modal
    header-close-content=""
    v-model="isOpen"
    @close="hideDialog"
    @cancel="hideDialog"
    :id="modalName"
    :busy="isBusy"
    :title="$t('CatalogMappingProject')"
    @ok="createMappingProject"
    @shown="init"
    no-close-on-backdrop
    no-close-on-esc
    data-cy="channel-creator-mapping-project-creation-modal">
    <div class="create-mapping-project-dialog" data-cy="create-mapping-project-dlg">
      <div class="row">
        <div class="col-lg-12">
          {{ $t("name") }}
        </div>
        <div class="col-lg-12 mt-2">
          <input
            :class="{ 'border-danger': nameValidationFeedback }"
            v-model="catalogMappingProjectName"
            v-tooltip="nameValidationFeedback"
            ref="focusTarget"
            class="form-control"
            data-cy="channel-creator-mapping-project-name-txt" />
        </div>
      </div>
      <div class="row mt-2">
        <div class="col-lg-12 pt-2">
          <div class="scrollable">
            <table class="table table-sm table-borderless" data-cy="channel-creator-mapping-project-targetsystem-tbl">
              <thead class="target-system-list">
                <tr>
                  <th>{{ $t("TargetSystem") }}</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="target in sortedMappingTargets" :key="target.catalogMappingTargetId">
                  <tr
                    @click="onMappingTargetChanged(target.catalogMappingTargetId)"
                    :class="[
                      {
                        selected: catalogMappingTargetId && catalogMappingTargetId == target.catalogMappingTargetId
                      }
                    ]">
                    <td>{{ target.name }}</td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    <template #modal-footer="{ ok, cancel }">
      <b-btn @click="ok" :disabled="isCreateDisabled" variant="primary" data-cy="channel-creator-mapping-project-save-btn">{{ $t("Save") }}</b-btn>
      <b-btn @click="cancel" data-cy="channel-creator-mapping-project-cancel-btn">{{ $t("Cancel") }}</b-btn>
    </template>
  </b-modal>
</template>

<script>
import { mapState, mapActions, mapMutations } from "vuex";
import catalogApi from "../../../services/catalogs";
import activityApi from "../../../services/activity";
import apps from "../../../services/applications";
import { setElementFocus } from "@/util/focusManager";
import { naturalSort } from "@util/naturalSort";

export default {
  props: ["showDialog", "modalName", "mappingProjects", "catalogId", "catalogName"],
  components: {},
  data() {
    return {
      catalogMappingProjectName: "",
      catalogMappingTargetId: "",
      catalogMappingTargetName: "",
      isBusy: false,
      isOpen: false
    };
  },

  computed: {
    ...mapState("metadata", ["contentLanguages"]),
    ...mapState("channelCreator", ["mappingTargets", "selectedTargetCatalogId", "modified"]),
    ...mapState("channelCreator", {
      mappingProjectsInStore: "mappingProjects",
      sortedMappingTargets() {
        return naturalSort(this.mappingTargets, "name");
      }
    }),
    ...mapState(["meta", "globalParameters"]),
    dirty: {
      get() {
        return this.modified.baseMapping;
      },
      set(nv) {
        this.markDirty({ section: "baseMapping", dirty: nv });
      }
    },
    nameValidationFeedback() {
      const isNameEmpty = this.catalogMappingProjectName == null || this.catalogMappingProjectName == "";
      const isDuplicateNameExists =
        this.catalogMappingProjectName &&
        this.mappingProjects.filter(
          (x) =>
            x.name &&
            x.name != "" &&
            this.catalogMappingProjectName &&
            this.catalogMappingProjectName != "" &&
            x.name.substr(0, x.name.lastIndexOf(" (")).toLowerCase() == this.catalogMappingProjectName.toLowerCase()
        ).length > 0;
      const isNameLengthExceeds = this.catalogMappingProjectName && this.catalogMappingProjectName.length > 256;

      if (isNameEmpty) return this.$t("validation.valueRequired", { field: this.$t("name") });
      else if (isDuplicateNameExists)
        return this.$t("validation.duplicateExists", {
          field: this.$t("name")
        });
      else if (isNameLengthExceeds)
        return this.$t("validation.lengthExceeds", {
          field: this.$t("name", 1),
          length: 256
        });
      else return null;
    },
    isCreateDisabled() {
      return !!this.nameValidationFeedback;
    },
    primaryLanguage() {
      return this.contentLanguages.enabled[0];
    },
    languageCode() {
      return this.contentLanguages.enabled[0].languageCode;
    }
  },
  mounted() {
    this.init();
  },
  methods: {
    ...mapMutations("channelCreator", ["markDirty"]),
    ...mapActions("channelCreator", ["addMappingProject"]),
    init: async function () {
      this.$nextTick(() => setElementFocus(this.$refs.focusTarget));
      this.catalogMappingProjectName = this.getName();
      this.catalogMappingTargetId = Object.values(this.sortedMappingTargets)[0]?.catalogMappingTargetId;
      this.catalogMappingTargetName = Object.values(this.sortedMappingTargets).filter((x) => x.catalogMappingTargetId === this.catalogMappingTargetId)[0]?.name;
    },
    getName() {
      const exp = () => /\(([^)]+)\)/;

      let numbers = [];
      if (this.mappingProjects) {
        numbers = this.mappingProjects
          .filter((x) => x.name && x.name != "" && x.name.substr(0, x.name.lastIndexOf(" (")).indexOf(this.$t("CatalogMappingProject") + " (") == 0)
          .map((x) => exp().exec(x.name.substr(0, x.name.lastIndexOf(" ("))))
          .filter((x) => x && x[1])
          .map((x) => parseInt(x[1]))
          .sort((a, b) => a - b);
      }
      const number = numbers.length && numbers.length != 0 ? numbers[numbers.length - 1] + 1 : 1;

      return this.$t("CatalogMappingProject") + " (" + number + ")";
    },
    onMappingTargetChanged(catalogMappingTargetId) {
      this.catalogMappingTargetId = catalogMappingTargetId;
      this.catalogMappingTargetName = Object.values(this.mappingTargets).filter((x) => x.catalogMappingTargetId === catalogMappingTargetId)[0]?.name;
    },
    createMappingProject: async function () {
      if (this.dirty) {
        if (!confirm(this.$t("confirmNavigation"))) return;
        else this.dirty = false;
      }
      this.isBusy = true;
      let mappingProject = null;

      if (this.catalogId) {
        if (this.catalogId == this.selectedTargetCatalogId) {
          const catalogMappingProjectId = await this.addMappingProject({
            catalogMappingProjectName: this.catalogMappingProjectName,
            catalogId: this.catalogId,
            catalogMappingTargetId: this.catalogMappingTargetId,
            catalogMappingTargetName: this.catalogMappingTargetName
          });
          this.createActivity(this.catalogMappingProjectName, this.catalogMappingTargetName);
          mappingProject = this.mappingProjectsInStore.find((prj) => prj.catalogMappingProjectId == catalogMappingProjectId);
        } else {
          const newCatalogMappingProject = await catalogApi.createCatalogMappingProject(
            this.catalogMappingProjectName,
            this.catalogId,
            this.catalogMappingTargetId
          );
          this.createActivity(newCatalogMappingProject.name, this.catalogMappingTargetName);
          mappingProject = {
            catalogMappingProjectId: newCatalogMappingProject.catalogMappingProjectId,
            catalogMappingTargetId: newCatalogMappingProject.catalogMappingTargetId,
            name: newCatalogMappingProject.name + " (" + this.catalogMappingTargetName + ")",
            targetSystemName: this.catalogMappingTargetName,
            targetSystem: newCatalogMappingProject.targetSystem
          };
        }
      }
      this.$emit("createdMappingProject", mappingProject);

      this.$emit("update:showDialog", false);
      this.isBusy = false;
    },
    createActivity: async function (projectName, targetSystemName) {
      try {
        if (this.globalParameters.IsActivityFeedEnabled) {
          const activityCriterias = [
            {
              severity: "Information",
              process: "Create",
              objectType: "MappingProject",
              objectValue: projectName ?? "",
              descriptionKey: "MappingProjectInformation",
              descriptionValues: [targetSystemName, this.catalogName],
              status: "Successful",
              action: "Completed",
              applicationId: apps.Modules.ChannelCreator.Id
            }
          ];
          await activityApi.createActivity(activityCriterias);
        }
      } catch (error) {
        console.error("Activity insertion for catalog mapping project creation process failed with error : ", error);
      }
    },
    hideDialog() {
      this.$emit("update:showDialog", false);
    }
  },
  watch: {
    showDialog(nv) {
      this.isOpen = nv;
    }
  }
};
</script>
